import { useContext, useState } from 'react';
import { GlobalContext } from '../../../context/GlobalContext';
import { unsafeAPI } from '../../../hooks/api';
import { Dialog } from '../../../components/Dialog';
import { Button } from '../../../shadcn/Button';
import Select from '../../../components/Select';
import Notice from '../../../components/Notice';
import { ArrowRightLeft } from 'lucide-react';
import { API_BASE } from '../../../utils/constants';
import TOTPAuth from '../../../components/TOTP';

interface ChangeStatusDialogProps {
	payoutIds: string[];
	onClose: () => void;
	onActionComplete: () => void;
}

interface ChangeStatusResponse {
	success: boolean;
	updated_count?: number;
	skipped?: { payout_id: string; reason: string }[];
	detail?: string;
}

const STATUS_OPTIONS = [
	{ label: 'Pendiente', value: 'pending' },
	{ label: 'Procesando', value: 'processing' },
	{ label: 'Completado', value: 'completed' },
	{ label: 'Fallido', value: 'failed' },
	{ label: 'Cancelado', value: 'cancelled' },
	{ label: 'Reintentando', value: 'retrying' },
];

export default function ChangeStatusDialog({ payoutIds, onClose, onActionComplete }: ChangeStatusDialogProps) {
	const { userData, internalCountry } = useContext(GlobalContext);

	const [newStatus, setNewStatus] = useState('');

	const [loading, setLoading] = useState(false);
	const [result, setResult] = useState<ChangeStatusResponse | null>(null);
	const [error, setError] = useState<string | null>(null);

	const handleChangeStatus = async (code: string) => {
		setLoading(true);
		setError(null);

		try {
			const response: ChangeStatusResponse = await unsafeAPI(
				'POST',
				`${API_BASE}/payments/merchant_payouts/change_status/`,
				{
					payout_ids: payoutIds,
					new_status: newStatus,
					confirmation_code: code,
				},
				userData,
				internalCountry?.get()
			);

			if (response?.success) {
				setResult(response);
			} else {
				setError(response?.detail || 'Error al cambiar el estatus');
			}
		} catch (err: any) {
			setError('Error: ' + err.message);
		} finally {
			setLoading(false);
		}
	};

	const handleClose = () => {
		if (result?.updated_count) onActionComplete();
		else onClose();
	};

	return (
		<Dialog title="Cambiar Estatus" onClose={handleClose} className="max-w-lg" noContentWrapper cerrarButton={false}>
			<div className="no-scrollbar space-y-4 overflow-y-auto p-1 leading-normal text-neutral-200">
				<div className="flex items-center gap-3 rounded-lg bg-blue-500/10 px-3 py-2">
					<ArrowRightLeft className="size-5 shrink-0 text-blue-400" />
					<p className="text-sm text-blue-300">
						Se cambiará el estatus de <strong>{payoutIds.length}</strong> payout{payoutIds.length !== 1 ? 's' : ''}. No
						se ejecutará ningún movimiento de dinero.
					</p>
				</div>

				{!result ? (
					<div className="flex flex-col gap-2">
						<Select
							options={STATUS_OPTIONS}
							onSelectedChange={(value) => setNewStatus(String(value))}
							placeholder="Selecciona el nuevo estatus"
							floatingLabel="Nuevo estatus"
							Icon={<ArrowRightLeft className="size-4 text-neutral-300" />}
						/>

						{newStatus && <TOTPAuth isSetUp={true} onSuccess={handleChangeStatus} callVerifyEndpoint={false} />}

						{error && <Notice message={error} variant="error" />}

						{loading && (
							<div className="flex items-center gap-2 text-sm text-neutral-400">
								<div className="h-4 w-4 animate-spin rounded-full border-2 border-neutral-600 border-t-primary-500" />
								Cambiando estatus...
							</div>
						)}

						<div className="flex justify-end border-t border-neutral-800 pt-4">
							<Button variant="secondary" className="w-fit" onClick={onClose} disabled={loading}>
								Cancelar
							</Button>
						</div>
					</div>
				) : (
					<>
						<Notice
							message={`${result.updated_count ?? payoutIds.length} payout${(result.updated_count ?? payoutIds.length) !== 1 ? 's' : ''} actualizado${(result.updated_count ?? payoutIds.length) !== 1 ? 's' : ''} correctamente.`}
							variant="success"
						/>

						{result.skipped && result.skipped.length > 0 && (
							<div className="space-y-2">
								<p className="text-sm font-medium text-neutral-300">Omitidos ({result.skipped.length}):</p>
								<div className="max-h-40 space-y-1 overflow-y-auto rounded-lg border border-neutral-800 bg-neutral-900/50 p-3">
									{result.skipped.map((item, idx) => (
										<p key={idx} className="text-xs text-neutral-400">
											<span className="font-mono text-neutral-300">{item.payout_id.slice(0, 8)}...</span>
											{' — '}
											{item.reason}
										</p>
									))}
								</div>
							</div>
						)}

						<div className="flex justify-end border-t border-neutral-800 pt-4">
							<Button variant="primary" className="w-fit" onClick={handleClose}>
								Cerrar
							</Button>
						</div>
					</>
				)}
			</div>
		</Dialog>
	);
}
